/**
 * Viewer for an E2E attachment inside a chat bubble: image thumbnail,
 * video player, voice note or downloadable file row. While the
 * binary is not decrypted ("ready") it shows the reception progress.
 *
 * It respects Susurro's visual language (theme-aware tokens, encryption teal) and
 * the reduced-motion guard (animation-free spinner when requested).
 */

import { Match, Show, Switch } from "solid-js";
import { Download, FileLock2, Loader2, Lock, Mic } from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { useChat } from "@/providers/chat-provider";
import { useI18n } from "@/providers/i18n-provider";
import type { MessageAttachment } from "@/lib/chat/types";

function formatBytes(n: number): string {
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms?: number): string | undefined {
  if (!ms) {
    return undefined;
  }
  const secs = Math.round(ms / 1000);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function AttachmentView(props: { attachment: MessageAttachment; outgoing: boolean }) {
  const chat = useChat();
  const { t } = useI18n();
  const att = () => props.attachment;
  const url = () => chat.attachmentUrl(att().attachId, att().mime);

  return (
    <div class="mt-0.5" data-testid="chat-attachment" data-kind={att().mediaKind}>
      <Show
        when={att().ready && url()}
        fallback={
          <Show
            when={!att().failed}
            fallback={
              <div class="flex items-center gap-2 rounded-xl border border-[var(--warning)]/30 bg-[var(--warning)]/10 px-3 py-2 text-xs text-[var(--warning-foreground,inherit)]">
                <FileLock2 class="h-4 w-4 shrink-0" />
                {t(msg`Attachment could not be verified`)}
              </div>
            }
          >
            <div
              class="flex items-center gap-2.5 rounded-xl border border-[var(--glass-border)] bg-[var(--field-background)] px-3 py-2.5 text-xs"
              data-testid="media-progress"
            >
              <Loader2 class="h-4 w-4 shrink-0 animate-spin text-success motion-reduce:animate-none" />
              <div class="min-w-0 flex-1">
                <div class="truncate font-medium text-foreground">{att().name}</div>
                <div class="text-muted-foreground">{t(msg`Receiving…`)}</div>
              </div>
              <Lock class="h-3.5 w-3.5 shrink-0 text-success" />
            </div>
          </Show>
        }
      >
        <Switch>
          <Match when={att().mediaKind === "image"}>
            <a href={url()} target="_blank" rel="noreferrer" class="block">
              <img
                data-testid="media-image"
                src={url()}
                alt={att().name}
                class="max-h-72 max-w-full rounded-xl border border-[var(--glass-border)] object-contain"
              />
            </a>
          </Match>
          <Match when={att().mediaKind === "video"}>
            <video
              data-testid="media-video"
              src={url()}
              controls
              class="max-h-72 max-w-full rounded-xl border border-[var(--glass-border)]"
            />
          </Match>
          <Match when={att().mediaKind === "voice"}>
            <div class="flex items-center gap-2.5 rounded-full border border-[var(--glass-border)] bg-[var(--field-background)] px-3 py-2">
              <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
                <Mic class="h-3.5 w-3.5" />
              </span>
              <audio data-testid="media-audio" src={url()} controls class="h-8 max-w-[220px]" />
              <Show when={formatDuration(att().durationMs)}>
                {(d) => <span class="shrink-0 text-[11px] text-muted-foreground">{d()}</span>}
              </Show>
            </div>
          </Match>
          <Match when>
            <a
              data-testid="media-download"
              href={url()}
              download={att().name}
              class="flex items-center gap-2.5 rounded-xl border border-[var(--glass-border)] bg-[var(--field-background)] px-3 py-2.5 text-xs no-underline transition-colors hover:bg-[var(--surface-secondary)]"
            >
              <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success/15 text-success">
                <Download class="h-4 w-4" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block truncate font-medium text-foreground">{att().name}</span>
                <span class="text-muted-foreground">{formatBytes(att().size)}</span>
              </span>
            </a>
          </Match>
        </Switch>
      </Show>
    </div>
  );
}
